import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { applyStoredBrandColors } from './brandColors';
import {
  applyColorModeClass,
  ColorMode,
  nextColorMode,
  persistColorMode,
  readStoredColorMode,
  ResolvedColorMode,
} from './colorMode';

type ColorModeContextValue = {
  mode: ColorMode;
  resolved: ResolvedColorMode;
  setMode: (mode: ColorMode) => void;
  cycleMode: () => void;
};

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const [mode, setModeState] = useState<ColorMode>(() => readStoredColorMode());

  const resolved = mode;

  useEffect(() => {
    applyColorModeClass(resolved, pathname || '/');
    applyStoredBrandColors();
  }, [resolved, pathname]);

  const setMode = useCallback((next: ColorMode) => {
    persistColorMode(next);
    setModeState(next);
  }, []);

  const cycleMode = useCallback(() => {
    setMode(nextColorMode(mode));
  }, [mode, setMode]);

  const value = useMemo(
    () => ({ mode, resolved, setMode, cycleMode }),
    [mode, resolved, setMode, cycleMode]
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

export const useColorMode = (): ColorModeContextValue => {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used within ThemeProvider');
  }
  return context;
};
